import { type IOverviewJointBillService } from "@services/overview/types";
import { api } from "@/api";
import {
  type IBooklet,
  type IBookletRequest,
  type ICreateJointBillServiceRequest,
  type IDeleteJointBillServiceRequest,
  type IEditJointBillServiceRequest,
  type IFile,
  type IJointBillServiceEditData,
  type IJointBillServicePeriodList,
  type IOpenJointBillServiceCurrentPeriodRequestData,
  type ITransferPreviousExpectedPaymentsRequest,
} from "./types";

export const createJointBillService = (
  data: ICreateJointBillServiceRequest,
): Promise<IOverviewJointBillService["jointBillServiceId"]> =>
  api
    .post<IOverviewJointBillService["jointBillServiceId"]>(
      "/joint-bill-services",
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    .then((r) => r.data);

export const editJointBillService = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  data: IEditJointBillServiceRequest,
): Promise<string> =>
  api
    .put<string>("/joint-bill-services", data, {
      headers: { jointBillServiceId },
    })
    .then((r) => r.data);

export const deleteJointBillService = async (
  data: IDeleteJointBillServiceRequest,
) =>
  api.delete<void>("/joint-bill-services", {
    headers: {
      jointBillServiceId: data.jointBillServiceId,
    },
  });

export const getJBSPeriodList = (): Promise<IJointBillServicePeriodList[]> =>
  api
    .get<{ jointBillServicePeriods: IJointBillServicePeriodList[] }>(
      "/joint-bill-services/periods",
    )
    .then((r) => r.data.jointBillServicePeriods);

export const getJBSPeriodPdf = ({
  jointBillServiceId,
  periodId,
}: {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
}): Promise<IFile> =>
  api
    .get<IFile>("/bills/pdf", {
      headers: {
        jointBillServiceId,
      },
      params: {
        PeriodId: periodId,
      },
    })
    .then((r) => r.data);

export const getJBSPeriodProjectPdf = ({
  jointBillServiceId,
  periodId,
}: {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
}): Promise<IFile> =>
  api
    .get<IFile>("/bills/project/pdf", {
      headers: {
        jointBillServiceId,
      },
      params: {
        PeriodId: periodId,
      },
    })
    .then((r) => r.data);

export const getJBSEditData = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
): Promise<IJointBillServiceEditData> =>
  api
    .get<IJointBillServiceEditData>("/joint-bill-services/edit", {
      headers: {
        jointBillServiceId,
      },
    })
    .then((r) => r.data);

export const openJointBillServiceCurrentPeriod = async (
  data: IOpenJointBillServiceCurrentPeriodRequestData,
) =>
  await api.post<void>(`/joint-bill-services/periods/current/open`, undefined, {
    headers: {
      jointBillServiceId: data.jointBillServiceId,
    },
  });

export const getProviderBillDetailizationHtml = ({
  billDetailizationUrl,
}: {
  billDetailizationUrl: string;
}) => api.get<string>(billDetailizationUrl).then((r) => r.data);

export const getBillBooklets = async (data: IBookletRequest) =>
  (await api.get<IBooklet>(data.url)).data;

export const transferPreviousExpectedPayments = async (
  data: ITransferPreviousExpectedPaymentsRequest,
) =>
  await api.put<void>(
    `/bills/${data.billId}/transfer-previous-expected-payments`,
  );
